<template>
  <v-container class="py-6">
    <h2 class="text-h5 font-weight-bold mb-4">Listings With Unread Messages</h2>

    <!-- Loading skeleton -->
    <v-card v-if="loading" elevation="2" class="mb-6">
      <v-card-title>Listings</v-card-title>
      <v-card-text>
        <div class="d-flex mb-2">
          <v-skeleton-loader type="text" width="20%" class="mr-6" />
          <v-skeleton-loader type="text" width="20%" class="mr-6" />
          <v-skeleton-loader type="text" width="20%" class="mr-6" />
          <v-skeleton-loader type="text" width="10%" class="ml-auto" />
        </div>
        <div v-for="n in itemsPerPage" :key="n" class="d-flex align-center py-3">
          <v-skeleton-loader type="text" width="20%" class="mr-6" />
          <v-skeleton-loader type="text" width="20%" class="mr-6" />
          <v-skeleton-loader type="text" width="20%" class="mr-6" />
          <v-skeleton-loader type="button" width="100" class="ml-auto" />
        </div>
      </v-card-text>
    </v-card>

    <!-- Empty state -->
    <v-alert v-else-if="listings.length === 0" type="info" variant="tonal">
      No listings with unread messages.
    </v-alert>

    <!-- Table -->
    <v-card v-else elevation="2" class="mb-6">
      <v-card-title>Listings</v-card-title>
      <v-card-text>
        <v-table>
          <thead>
            <tr>
              <th class="text-left">Listing ID</th>
              <th class="text-left">Current Status</th>
              <th class="text-left">Unread Messages</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in pagedItems" :key="item.id">
              <td>{{ item.booking_id }}</td>
              <td>
                <v-chip
                  :color="getStatusColor(item.status)"
                  :text-color="getStatusTextColor(item.status)"
                  size="small"
                  class="text-capitalize"
                  variant="flat"
                >
                  {{ getDisplayStatus(item.status) }}
                </v-chip>
              </td>
              <td>
                <v-chip color="error" variant="tonal" label>{{ item.unread_count }}</v-chip>
              </td>
              <td class="text-right">
                <v-btn
                  v-if="isAgent"
                  color="secondary"
                  variant="outlined"
                  size="small"
                  :to="`/dashboard/listings/${item.id}`"
                >
                  View
                </v-btn>
                <v-btn
                  v-else-if="isAdmin"
                  color="secondary"
                  variant="outlined"
                  size="small"
                  :to="`/dashboard/listings/admin/${item.id}`"
                >
                  View
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Pagination -->
        <v-row class="mt-4" align="center" justify="space-between">
          <v-col cols="auto">
            <small class="text-medium-emphasis">
              Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ listings.length }}
            </small>
          </v-col>
          <v-col cols="auto" class="d-flex align-center ga-4">
            <v-select
              v-model="itemsPerPage"
              :items="[5, 10, 25, 50]"
              label="Rows"
              hide-details
              density="compact"
              variant="outlined"
              style="max-width: 120px"
            />
            <v-pagination
              v-model="page"
              :length="totalPages"
              :total-visible="7"
              density="comfortable"
              prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'

export default {
  name: 'UserMessagesView',
  data() {
    return {
      loading: true,
      listings: [],
      // client-side pagination
      page: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    isAdmin() { return this.$store.state.user?.isAdmin ?? false },
    isAgent() { return this.$store.state.user?.is_agent ?? false },

    totalPages() {
      return Math.max(1, Math.ceil(this.listings.length / this.itemsPerPage))
    },
    pageStart() {
      return (this.page - 1) * this.itemsPerPage
    },
    pageEnd() {
      return Math.min(this.pageStart + this.itemsPerPage, this.listings.length)
    },
    pagedItems() {
      return this.listings.slice(this.pageStart, this.pageEnd)
    }
  },
  async mounted() {
    await this.loadUnreadListings()
  },
  methods: {
    async loadUnreadListings() {
      try {
        const userId = this.$store.state.user?.uid
        if (!userId) { this.loading = false; return }

        const { data: counts, error } = await supabase
          .rpc('get_unread_message_counts', { user_id: userId })
        if (error) throw error

        const listingIds = (counts || []).map(c => c.listing_id)
        if (!listingIds.length) {
          this.listings = []
          return
        }

        const { data: listingsData, error: listingError } = await supabase
          .from('listings')
          .select('*')
          .in('id', listingIds)
        if (listingError) throw listingError

        this.listings = (listingsData || []).map(l => ({
          ...l,
          unread_count: counts.find(c => c.listing_id === l.id)?.unread_count || 0
        }))

        // reset page if out of range after load
        if (this.page > this.totalPages) this.page = this.totalPages
      } catch (e) {
        console.error('Failed to load unread listings:', e)
        this.listings = []
      } finally {
        this.loading = false
      }
    },

    getDisplayStatus(status) {
      const map = {
        pending: 'Pending Approval',
        bookingpending: 'Booking Pending',
        denied: 'Denied',
        confirmed: 'Confirmed',
        cancelled: 'Cancelled',
        expired: 'Expired',
        active: 'Active',
        archived: 'Archived'
      }
      return map[status] || 'Unknown'
    },
    getStatusColor(status) {
      const colors = {
        pending: 'amber-lighten-4',
        bookingpending: 'amber-lighten-4',
        confirmed: 'green-lighten-4',
        denied: 'red-lighten-4',
        cancelled: 'red-lighten-4',
        expired: 'grey-lighten-3',
        active: 'blue-lighten-4',
        archived: 'grey-lighten-4'
      }
      return colors[status] || 'grey-lighten-4'
    },
    getStatusTextColor(status) {
      const colors = {
        pending: 'amber-darken-4',
        bookingpending: 'amber-darken-4',
        confirmed: 'green-darken-4',
        denied: 'red-darken-4',
        cancelled: 'red-darken-4',
        expired: 'grey-darken-2',
        active: 'blue-darken-4',
        archived: 'grey-darken-2'
      }
      return colors[status] || 'grey-darken-3'
    }
  }
}
</script>

<style scoped>
/* small niceties */
.v-table thead th { font-weight: 600; }
</style>
